import React, { useState } from "react"
import OutsideClickHandler from "react-outside-click-handler"
import dayjs from "dayjs"

import {
  Container,
  Dropdown,
  Empty,
  RestoreButton,
  Row,
  RowBadges,
  RowLabel,
  RowMeta,
  StatusBadge,
  Trigger
} from "./RevisionSelector.styled"

import { ReactComponent as DownIcon } from "images/icons/chevron-down.svg"

export type RevisionSummary = {
  id: string
  revision: number
  name: string
  trigger: string
  created: string
  updated: string
}

type Props = {
  revisions: RevisionSummary[]
  currentDraftRevision: number
  publishedRevision: number | null
  viewingRevision: number | null
  restoredFromRevision: number | null
  onView: (revision: number) => void
  onRestore: (revision: number) => void
  onReturnToDraft: () => void
}

const RevisionSelector = ({
  revisions,
  currentDraftRevision,
  publishedRevision,
  viewingRevision,
  restoredFromRevision,
  onView,
  onRestore,
  onReturnToDraft
}: Props) => {
  const [open, setOpen] = useState(false)
  const isViewing = viewingRevision !== null
  const isRestored = restoredFromRevision !== null
  const triggerLabel = isViewing
    ? `Viewing v${viewingRevision}`
    : isRestored
      ? `Rev ${currentDraftRevision + 1} (from v${restoredFromRevision})`
      : `Rev ${currentDraftRevision}`

  // Sort by revision desc, breaking ties by updated desc so the most recent
  // snapshot per revision number comes first.
  const sorted = [...revisions].sort((a, b) => {
    if (b.revision !== a.revision) return b.revision - a.revision
    return b.updated.localeCompare(a.updated)
  })

  const currentDraftRow = sorted.find((r) => r.revision === currentDraftRevision)
  const isCurrentDraftRow = (r: RevisionSummary) =>
    currentDraftRow !== undefined &&
    r.revision === currentDraftRow.revision &&
    r.updated === currentDraftRow.updated

  const handleRowClick = (rev: RevisionSummary) => {
    setOpen(false)
    if (isCurrentDraftRow(rev)) {
      // Selecting the current draft means: return to the draft view.
      if (isViewing) onReturnToDraft()
      return
    }
    onView(rev.revision)
  }

  const handleRestore = (
    e: React.MouseEvent<HTMLSpanElement>,
    rev: RevisionSummary
  ) => {
    e.stopPropagation()
    setOpen(false)
    onRestore(rev.revision)
  }

  return (
    <Container>
      <OutsideClickHandler onOutsideClick={() => setOpen(false)}>
        <Trigger
          type="button"
          $viewing={isViewing || isRestored}
          aria-haspopup="listbox"
          aria-expanded={open}
          onClick={() => setOpen((v) => !v)}
        >
          {triggerLabel}
          <DownIcon />
        </Trigger>
        {open && (
          <Dropdown role="listbox">
            {sorted.length === 0 && (
              <Empty>No revisions yet — save the workflow to create one.</Empty>
            )}
            {sorted.map((rev) => {
              const isCurrentDraft = isCurrentDraftRow(rev)
              const isPublished =
                publishedRevision !== null && rev.revision === publishedRevision
              const isRestoredSource = isRestored && rev.revision === restoredFromRevision
              const isActive = isViewing
                ? rev.revision === viewingRevision
                : isRestoredSource || (!isRestored && isCurrentDraft)

              return (
                <Row
                  key={`${rev.id}-${rev.revision}`}
                  type="button"
                  $active={isActive}
                  onClick={() => handleRowClick(rev)}
                  aria-label={
                    isCurrentDraft
                      ? `Current draft revision ${rev.revision}`
                      : `View revision ${rev.revision}`
                  }
                >
                  <RowLabel>v{rev.revision}</RowLabel>
                  <RowMeta>{dayjs(rev.updated).format("YYYY-MM-DD HH:mm")}</RowMeta>
                  <RowBadges>
                    {isPublished && <StatusBadge $published>Published</StatusBadge>}
                    {isCurrentDraft && <StatusBadge $draft>Current draft</StatusBadge>}
                    {!isCurrentDraft && isViewing && isActive && (
                      <StatusBadge $viewing>Viewing</StatusBadge>
                    )}
                    {!isCurrentDraft && isRestoredSource && (
                      <StatusBadge $viewing>Restored from</StatusBadge>
                    )}
                    {!isCurrentDraft && (
                      <RestoreButton
                        role="button"
                        tabIndex={0}
                        aria-label={`Restore revision ${rev.revision} as draft`}
                        onClick={(e) => handleRestore(e, rev)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter" || e.key === " ") {
                            e.preventDefault()
                            handleRestore(
                              e as unknown as React.MouseEvent<HTMLSpanElement>,
                              rev
                            )
                          }
                        }}
                      >
                        Restore
                      </RestoreButton>
                    )}
                  </RowBadges>
                </Row>
              )
            })}
          </Dropdown>
        )}
      </OutsideClickHandler>
    </Container>
  )
}

export default RevisionSelector
